import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as apiKeysApi from '../../api/apiKeys';
import { generateTraceId, logAction } from '../../utils/trace';

// Async thunks
export const fetchAPIKeys = createAsyncThunk(
  'apiKeys/fetchAPIKeys',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'apiKeys/fetchAPIKeys/pending', meta: { traceId } });
      const data = await apiKeysApi.listAPIKeys({ traceId });
      logAction({ type: 'apiKeys/fetchAPIKeys/fulfilled', meta: { traceId } }, {
        keyCount: data.keys?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'apiKeys/fetchAPIKeys/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const createAPIKey = createAsyncThunk(
  'apiKeys/createAPIKey',
  async ({ name, description, scopes, expiresInDays, tenantId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'apiKeys/createAPIKey/pending', meta: { traceId } }, {
        name,
        scopes,
        tenantId,
      });
      const data = await apiKeysApi.createAPIKey({
        name,
        description,
        scopes,
        expires_in_days: expiresInDays,
        tenant_id: tenantId,
      }, { traceId });
      logAction({ type: 'apiKeys/createAPIKey/fulfilled', meta: { traceId } }, {
        keyId: data.key_id,
        keyPrefix: data.key_prefix,
      });
      return data;
    } catch (error) {
      logAction({ type: 'apiKeys/createAPIKey/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const revokeAPIKey = createAsyncThunk(
  'apiKeys/revokeAPIKey',
  async ({ keyId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'apiKeys/revokeAPIKey/pending', meta: { traceId } }, {
        keyId,
      });
      await apiKeysApi.revokeAPIKey(keyId, { traceId });
      logAction({ type: 'apiKeys/revokeAPIKey/fulfilled', meta: { traceId } }, {
        keyId,
      });
      return keyId;
    } catch (error) {
      logAction({ type: 'apiKeys/revokeAPIKey/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const rotateAPIKey = createAsyncThunk(
  'apiKeys/rotateAPIKey',
  async ({ keyId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'apiKeys/rotateAPIKey/pending', meta: { traceId } }, {
        keyId,
      });
      const data = await apiKeysApi.rotateAPIKey(keyId, { traceId });
      logAction({ type: 'apiKeys/rotateAPIKey/fulfilled', meta: { traceId } }, {
        oldKeyId: keyId,
        newKeyId: data.new_key?.key_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'apiKeys/rotateAPIKey/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

const apiKeysSlice = createSlice({
  name: 'apiKeys',
  initialState: {
    keys: [],
    total: 0,
    isLoading: false,
    error: null,
    // Store newly created key secret (shown only once)
    newKeySecret: null,
    // Store rotation result (both old and new key info)
    rotationResult: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearNewKeySecret: (state) => {
      state.newKeySecret = null;
    },
    clearRotationResult: (state) => {
      state.rotationResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAPIKeys
      .addCase(fetchAPIKeys.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAPIKeys.fulfilled, (state, action) => {
        state.isLoading = false;
        state.keys = action.payload.keys || [];
        state.total = action.payload.total || 0;
      })
      .addCase(fetchAPIKeys.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // createAPIKey
      .addCase(createAPIKey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.newKeySecret = null;
      })
      .addCase(createAPIKey.fulfilled, (state, action) => {
        state.isLoading = false;
        // Store the secret key (shown only once)
        state.newKeySecret = action.payload.secret_key;
        // Add the key to the list (without secret)
        const { secret_key, ...keyWithoutSecret } = action.payload;
        state.keys.unshift(keyWithoutSecret);
        state.total += 1;
      })
      .addCase(createAPIKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // revokeAPIKey
      .addCase(revokeAPIKey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(revokeAPIKey.fulfilled, (state, action) => {
        state.isLoading = false;
        // Mark the key as revoked in the list
        const keyId = action.payload;
        const key = state.keys.find(k => k.key_id === keyId);
        if (key) {
          key.revoked_at = new Date().toISOString();
        }
      })
      .addCase(revokeAPIKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // rotateAPIKey
      .addCase(rotateAPIKey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.rotationResult = null;
      })
      .addCase(rotateAPIKey.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rotationResult = action.payload;
        // Update the old key's expiry in the list
        const oldKeyId = action.payload.old_key_id;
        const oldKey = state.keys.find(k => k.key_id === oldKeyId);
        if (oldKey) {
          oldKey.expires_at = action.payload.old_expires_at;
        }
        // Add the new key to the list
        if (action.payload.new_key) {
          const { secret_key, ...newKeyWithoutSecret } = action.payload.new_key;
          state.keys.unshift(newKeyWithoutSecret);
          state.total += 1;
        }
      })
      .addCase(rotateAPIKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearNewKeySecret, clearRotationResult } = apiKeysSlice.actions;
export default apiKeysSlice.reducer;
